import React, { useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import styled, { css } from 'styled-components'
import config from 'constants/config'
import zIndex from 'styles/tools/z-index'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { goBack } from 'connected-react-router'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import CommonHeaderCheckout from './CommonHeaderCheckout'
import StepsHeaderCheckout from './StepsHeaderCheckout/StepsHeaderCheckout'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { getTourCheckoutSteps } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import LayoutContainer from 'components/Common/LayoutContainer'
import CheckoutSubheader from 'checkout/Components/Shared/Headers/CheckoutSubheader'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import SecurePaymentHeaderCheckout from './SecurePaymentHeaderCheckout'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { useFlightDateChangeBackNavigation } from 'hooks/Flights/useFlightDateChangeBackNavigation'

const StickyContainer = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  z-index: ${zIndex.stickyHeader};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  ${() => !config.WL_USES_GLOBAL_STYLING && config.header.backgroundColor && css`
    background: ${config.header.backgroundColor};
    color: ${props => props.theme.palette.neutral.default.eight};
  `}

  ${() => !config.WL_USES_GLOBAL_STYLING && config.header.backgroundGradientColor && css`
    background: linear-gradient(90deg, ${config.header.backgroundColor}, ${config.header.backgroundGradientColor});
  `}
`

interface Props {
  isCruise: boolean
  hasTourCheckoutSteps: boolean
  checkoutMode?: App.CheckoutCartMode
}

function HeaderCheckout(props: Props) {
  const { isCruise, hasTourCheckoutSteps, checkoutMode } = props

  const dispatch = useAppDispatch()
  const { handleBack: handleFlightDateChangeBack } = useFlightDateChangeBackNavigation()

  const handleGoBack = useCallback(() => {
    if (checkoutMode === 'flight-change-dates') {
      handleFlightDateChangeBack()
    } else {
      dispatch(goBack())
    }
  }, [dispatch, checkoutMode, handleFlightDateChangeBack])

  const headerRef = useRef<HTMLElement>(null)

  useHeaderStateObserver('checkout', headerRef)

  const hasSteps = isCruise || hasTourCheckoutSteps || checkoutMode === 'flight-change-dates'

  useAppHeaderObserver(headerRef, 'sticky')

  const isSecurePayment = useAppSelector(state => state.router.location.pathname.includes('/pay'))

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.checkout}>
      <StickyContainer data-testid="checkout-header" ref={headerRef}>
        <LayoutContainer size="xl">
          {isSecurePayment && <SecurePaymentHeaderCheckout />}
          {!hasSteps && !isSecurePayment && <CommonHeaderCheckout onBack={handleGoBack} />}
          {hasSteps && !isSecurePayment && <StepsHeaderCheckout onBack={handleGoBack} />}
        </LayoutContainer>
      </StickyContainer>
      <CheckoutSubheader />
    </AnalyticsPageContext.Provider>
  )
}

const mapStateToProps = (state: App.State) => ({
  isCruise: getCruiseItems(state).length > 0,
  hasTourCheckoutSteps: getTourCheckoutSteps(state).length > 1,
  checkoutMode: findPostPurchaseCheckout(state.checkout.cart.mode) || '',
})

export default connect(mapStateToProps)(HeaderCheckout)
